import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Youtube } from "lucide-react";
import { useState } from "react";
import { api, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Blisson circle.");
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email.");
    }
  };

  return (
    <footer className="bg-[#F8F5F0] mt-32 pt-24 pb-10 border-t border-[#E5E0D8]" data-testid="footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-12 gap-10">
        <div className="col-span-2 md:col-span-4">
          <img src={LOGO_URL} alt="Blisson Professional" className="h-16 mb-6 mix-blend-multiply" />
          <p className="text-sm text-[#5C5C5C] leading-relaxed max-w-xs">
            Salon-grade beauty rituals, redesigned for the home. Crafted in small batches in India.
          </p>
          <div className="flex items-center gap-4 mt-8" data-testid="footer-socials">
            <a
              href="https://www.instagram.com/blisson.in"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              aria-label="Instagram"
              data-testid="footer-social-instagram"
            >
              <Instagram size={15} strokeWidth={1.4} />
            </a>
            <a
              href="#"
              className="w-10 h-10 border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              aria-label="Facebook"
              data-testid="footer-social-facebook"
            >
              <Facebook size={15} strokeWidth={1.4} />
            </a>
            <a
              href="#"
              className="w-10 h-10 border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              aria-label="Twitter"
              data-testid="footer-social-twitter"
            >
              <Twitter size={15} strokeWidth={1.4} />
            </a>
            <a
              href="#"
              className="w-10 h-10 border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              aria-label="YouTube"
              data-testid="footer-social-youtube"
            >
              <Youtube size={15} strokeWidth={1.4} />
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-5">Shop</div>
          <ul className="space-y-3 text-sm text-black/80">
            <li><Link to="/shop?cat=Hair%20Care" data-testid="footer-link-hair">Hair Care</Link></li>
            <li><Link to="/shop?cat=Skin%20Care" data-testid="footer-link-skin">Skin Care</Link></li>
            <li><Link to="/shop?cat=Facial%20Kits" data-testid="footer-link-facial">Facial Kits</Link></li>
            <li><Link to="/shop?cat=Serums" data-testid="footer-link-serums">Serums</Link></li>
            <li><Link to="/shop?cat=Wax%20Products" data-testid="footer-link-wax">Wax Products</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-5">House</div>
          <ul className="space-y-3 text-sm text-black/80">
            <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-[#C9A961] transition-colors">Contact Us</Link></li>
            <li><Link to="/salon-partner" data-testid="footer-link-salon" className="hover:text-[#C9A961] transition-colors">Salon Partner</Link></li>
            <li><Link to="/track-order" data-testid="footer-link-track" className="hover:text-[#C9A961] transition-colors">Track Order</Link></li>
            <li><Link to="/shipping-policy" data-testid="footer-link-shipping" className="hover:text-[#C9A961] transition-colors">Shipping Policy</Link></li>
            <li><Link to="/refund-policy" data-testid="footer-link-refund" className="hover:text-[#C9A961] transition-colors">Refunds & Returns</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline mb-5">The Blisson Circle</div>
          <p className="text-sm text-[#5C5C5C] mb-5">
            New rituals, exclusive launches and salon invitations — direct to your inbox.
          </p>
          <form onSubmit={subscribe} className="flex border-b border-black" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent py-3 outline-none text-sm placeholder:text-black/40"
              data-testid="newsletter-email-input"
              required
            />
            <button
              type="submit"
              className="text-[11px] tracking-[0.28em] uppercase font-medium hover:text-[#C9A961] transition-colors"
              data-testid="newsletter-submit"
            >
              Join →
            </button>
          </form>
          <div className="mt-10 text-xs text-[#5C5C5C] space-y-1.5 leading-relaxed">
            <div><a href="mailto:Theblisson@gmail.com" className="hover:text-[#C9A961] transition-colors" data-testid="footer-email">Theblisson@gmail.com</a></div>
            <div><a href="tel:+917383899003" className="hover:text-[#C9A961] transition-colors" data-testid="footer-phone">+91 73838 99003</a></div>
            <div className="not-italic pt-1" data-testid="footer-address">
              T-10, 3rd Floor, Capital Market,<br />
              Ravapar Road, Morbi — 363641
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 mt-16 pt-6 border-t border-[#E5E0D8] flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#5C5C5C]">
        <div>© {new Date().getFullYear()} Blisson Professional. Crafted with intention.</div>
        <div className="flex items-center gap-6">
          <a href="#">Privacy</a>
          <a href="#">Terms</a>
          <a href="#">Shipping & Returns</a>
        </div>
      </div>
    </footer>
  );
}
